/**
 * The sky, as a single function of view direction.
 *
 * Rather than generate an environment map, the same `skyColor` node is used
 * three ways: to paint the skybox, to light the reflection term in the water
 * shader, and to pick the fog colour. Because the water reflects the *actual*
 * sky function rather than a blurred capture of it, the horizon joins up
 * seamlessly and the sun glitter lands exactly where the sun is.
 */

import { Color, Mesh, MeshBasicNodeMaterial, SphereGeometry, Vector3 } from 'three/webgpu'
import { Fn, cameraPosition, color, mix, normalize, positionWorld, uniform, vec3 } from 'three/tsl'
import type { TSLNode } from './tslTypes.ts'

/** Mid-afternoon, sun off the starboard bow and high enough to read the sea by. */
export const SUN_DIRECTION = new Vector3(0.55, 0.46, -0.7).normalize()

export const sunDirectionUniform = uniform(SUN_DIRECTION)

const ZENITH = new Color('#1b4b86')
const HORIZON = new Color('#a8c2d4')
const GROUND = new Color('#25374a')
const SUN_TINT = new Color('#fff2d6')

/** Colour used for distance fog and for the far edge of the ocean. */
export const HAZE_COLOR = new Color('#9db6c8')
/** What the haze becomes under a storm sky. */
export const STORM_HAZE_COLOR = new Color('#4c565e')

/**
 * Weather inputs, written by the Weather controller each frame. Because the
 * ocean's reflection term samples `skyColor`, darkening the sky here darkens
 * the water in the same breath, and a lightning flash glitters off the waves
 * without any water-side code knowing about it.
 */
export const stormUniform = uniform(0)
export const lightningUniform = uniform(0)

const STORM_ZENITH = new Color('#2e3940')
const STORM_HORIZON = new Color('#556069')

/**
 * Sky radiance for a unit view direction.
 *
 * Three terms: a vertical gradient from haze to zenith, a broad forward-scatter
 * glow around the sun, and the sun disc itself. Below the horizon it fades to a
 * dull ground tone, which is what the underside of a wave sees.
 */
export const skyColor = /*#__PURE__*/ Fn(([direction]: [TSLNode]) => {
  const dir = normalize(direction)

  const gradient = dir.y.max(0).pow(0.42)
  const sky = mix(color(HORIZON), color(ZENITH), gradient).toVar()

  const sunAmount = dir.dot(sunDirectionUniform).max(0)
  // Wide haze bloom, then a tight disc. The disc exponent is deliberately huge
  // so it stays a crisp ~0.5-degree ball instead of a soft blob.
  const bloom = sunAmount.pow(6).mul(0.22).add(sunAmount.pow(64).mul(0.35))
  const disc = sunAmount.pow(3200).mul(14)
  sky.addAssign(color(SUN_TINT).mul(bloom.add(disc)))

  // Thicken the haze right at the horizon so distant geometry sinks into it.
  const horizonHaze = dir.y.abs().oneMinus().pow(8).mul(0.35)
  sky.assign(mix(sky, color(HAZE_COLOR), horizonHaze))

  // Storm: collapse the whole dome toward flat grey — sun, blue, and haze all
  // fold into it, which is what an overcast actually does to the light.
  const stormSky = mix(color(STORM_HORIZON), color(STORM_ZENITH), gradient)
  sky.assign(mix(sky, stormSky, stormUniform.mul(0.92)))

  // Lightning: a whole-dome whitening. The pulse shape lives on the CPU.
  sky.addAssign(vec3(1.0, 0.98, 0.92).mul(lightningUniform.mul(1.6)))

  const aboveHorizon = dir.y.smoothstep(-0.05, 0.02)
  return mix(color(GROUND), sky, aboveHorizon)
})

/**
 * A skybox that rides along with the camera. Depth writing is off and it draws
 * first, so everything else simply paints over it.
 */
export class Sky {
  readonly mesh: Mesh

  constructor() {
    const material = new MeshBasicNodeMaterial({ depthWrite: false, fog: false })
    material.side = 1 // BackSide
    material.colorNode = skyColor(positionWorld.sub(cameraPosition))

    this.mesh = new Mesh(new SphereGeometry(4000, 48, 24), material)
    this.mesh.frustumCulled = false
    this.mesh.renderOrder = -1000
    this.mesh.name = 'sky'
  }

  update(cameraPositionWorld: Vector3): void {
    this.mesh.position.copy(cameraPositionWorld)
  }
}

/** Sky radiance for a direction, evaluated on the CPU. Used to tint lights. */
export function skyColorCPU(dir: Vector3, target: Color): Color {
  const gradient = Math.pow(Math.max(dir.y, 0), 0.42)
  target.copy(HORIZON).lerp(ZENITH, gradient)
  const sunAmount = Math.max(dir.dot(SUN_DIRECTION), 0)
  const bloom = Math.pow(sunAmount, 6) * 0.22 + Math.pow(sunAmount, 64) * 0.35
  target.add(new Color().copy(SUN_TINT).multiplyScalar(bloom))
  return target
}
